// src/components/Header.tsx
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Stack,
  Container,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Email,
  Search,
  AccountCircle,
  Menu as MenuIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../hooks/useAuth";
import UserMenu from "../userMenu";
import { DOCUMENTS_Feature, PAYMENTS_PORTAL_Feature, SEARCH_Feature } from "@/features.config";
import SearchModal from "../../../search/SearchModal";

const Header: React.FC = () => {
  const { isAuthenticated, userRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchModalOpen, setSearchModalOpen] = useState(false);

  // Navigation items configuration
  const navItems = [
    { label: "Inicio", path: "/", needAuth: false, roles: ["all"] },
    { label: "Institucional", path: "/institutional", needAuth: false, roles: ["all"] },
    { label: "Sedes", path: "/sites", needAuth: false, roles: ["all"] },
    ...(DOCUMENTS_Feature === 'true' ? [{ label: "Documentos", path: "/documents", needAuth: true, roles: ["admin", "educator", "superadmin", "dir"] }] : []),
    ...(PAYMENTS_PORTAL_Feature === 'true' ? [{ label: "Portal de Pagos", path: "/payments/portal", needAuth: true, roles: ["attendant", "student"] }] : []),
  ];

  // Helper function to check if a nav item is active
  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  // Handle mobile menu toggle
  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Handle navigation and close mobile menu
  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  // Handle user menu open/close
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleSearchOpen = () => {
    setSearchModalOpen(true);
  };

  const handleSearchClose = () => {
    setSearchModalOpen(false);
  };

  const validateNavOptions = (item: any) => {

    if (!item.needAuth) {
      return true
    }
    ;

    if (item.needAuth && isAuthenticated) {
      if (item.roles.includes(userRole)) {
        return true
      }
    }
    return false
  }

  return (
    <>
      {/* Top Bar - Hidden on mobile */}
      <Box
        sx={{
          backgroundColor: "primary.dark",
          color: "white",
          py: 1,
          display: { xs: "none", md: "block" },
        }}
      >
        <Container maxWidth="xl">
          <Stack
            direction="row"
            spacing={3}
            justifyContent="flex-start"
            alignItems="center"
            sx={{ fontSize: "0.875rem" }}

          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Email sx={{ fontSize: 16 }} />
              <Typography variant="body2">contacto@jecaro.co</Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Main Navigation */}
      <AppBar
        position="static"
        elevation={0.5}
        sx={{
          backgroundColor: "white",
          height: "80px",
          justifyContent: "center",
          alignContent: "space-between",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{
            justifyContent: "space-between",
            py: 1,
            width: "100%",
          }}>
            {/* Logo */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ width: { xs: "auto", md: "30%" } }}
            >
              <Box
                component="img"
                src="/images/Logo.jpg"
                alt="Logo"
                sx={{
                  width: { xs: 40, md: 50 },
                  height: { xs: 40, md: 50 },
                  borderRadius: 1,
                }}
              />
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "primary.dark",
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  INSTITUCIÓN EDUCATIVA
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "primary.dark",
                    fontWeight: 400,
                  }}
                >
                  JOSÉ EUSEBIO CARO
                </Typography>
              </Box>
            </Stack>

            {/* Desktop Navigation Links */}
            <Stack
              direction="row"
              alignItems="center"
              justifyItems="space-between"
              sx={{
                width: "100%",
                display: { xs: "none", md: "flex" },
              }}
            >
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  validateNavOptions(item) ?
                    <Typography
                      key={item.path}
                      variant="body2"
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        color: active ? "primary.main" : "primary.dark",
                        cursor: "pointer",
                        fontWeight: active ? 600 : 500,
                        "&:hover": {
                          color: "primary.main",
                          fontWeight: 600,
                        },
                        width: item.label.length > 14 ? "30%" : "20%",
                        borderRight: { xs: "none", md: "1px solid #e0e0e0" },
                        textAlign: "center",
                      }}
                    >
                      {item.label}
                    </Typography> : null
                );
              })}

              <Box
                sx={{
                  width: "50%",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {/* Search Icon */}
                {SEARCH_Feature && SEARCH_Feature === 'true' && (
                  <IconButton
                    size="small"
                    sx={{ color: "primary.dark" }}
                    onClick={handleSearchOpen}
                  >
                    <Search />
                  </IconButton>
                )}

                {isAuthenticated ? (
                  <IconButton
                    onClick={handleUserMenuOpen}
                    sx={{
                      backgroundColor: "secondary.main",
                      borderRadius: 1,
                      width: 40,
                      height: 40,
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    }}
                  >
                    <AccountCircle sx={{ color: "white" }} />
                  </IconButton>
                ) :  (
                  <Button
                    variant="contained"
                    onClick={() => handleNavigate("/login")}
                    sx={{
                      backgroundColor: "secondary.main",
                      color: "white",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { backgroundColor: "primary.dark" },
                    }}
                  >
                    Ingresar
                  </Button> 
                )}
              </Box>
            </Stack>

            {/* Mobile Menu Button and Actions */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ display: { xs: "flex", md: "none" } }}
            >
              {/* Search Icon */}
              {SEARCH_Feature && SEARCH_Feature === 'true' && (
                <IconButton
                  size="small"
                  sx={{ color: "primary.dark" }}
                  onClick={handleSearchOpen}
                >
                  <Search />
                </IconButton>
              )}

              {/* User/Login Button */}
              {isAuthenticated ? (
                <IconButton
                  onClick={handleUserMenuOpen}
                  sx={{
                    backgroundColor: "secondary.main",
                    borderRadius: 1,
                    width: 40,
                    height: 40,
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  }}
                >
                  <AccountCircle sx={{ color: "white" }} />
                </IconButton>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleNavigate("/login")}
                  sx={{
                    backgroundColor: "secondary.main",
                    color: "white",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    px: 1.5,
                    "&:hover": { backgroundColor: "primary.dark" },
                  }}
                >
                  Ingresar
                </Button>
              )}

              {/* Hamburger Menu Icon */}
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuToggle}
                sx={{ color: "primary.dark" }}
              >
                {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: 280,
            backgroundColor: "white",
          },
        }}
      >
        <Box sx={{ pt: 2, px: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography
              variant="h6"
              sx={{ color: "primary.dark", fontWeight: 600 }}
            >
              Menú
            </Typography>
            <IconButton onClick={handleMobileMenuToggle}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
        </Box>

        <List>
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              validateNavOptions(item) ? <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    py: 2,
                    backgroundColor: active ? "primary.light" : "transparent",
                    "&:hover": {
                      backgroundColor: "primary.light",
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    slotProps={{
                      primary: {
                        fontWeight: active ? 600 : 500,
                        color: active ? "white" : "primary.dark",
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem> :null
            );
          })}
        </List>

        <Divider />

        {/* Contact Info in Mobile Menu */}
        <Box sx={{ px: 2, py: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "primary.dark", fontWeight: 600, mb: 2 }}
          >
            Contacto
          </Typography>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Email sx={{ fontSize: 18, color: "primary.main" }} />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                contacto@jecaro.co
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Drawer>

      {/* User Menu */}
      <UserMenu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
      />

      {/* Search Modal */}
      <SearchModal open={searchModalOpen} onClose={handleSearchClose} />
    </>
  );
};

export default Header;
